import CategoryTableRow from "./CategoryTableRow";
import ModernTable from "../shared/ModernTable";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { CategoryResult } from "../../interfaces/ICompetitions";
import { useMemo, useState } from "react";

interface CategoriesTableProps {
  categories: CategoryResult[];
  competitionId: string;
  onRowClick: (category: CategoryResult) => void;
  onBack: () => void;
  emptyMessage?: string;
  availableDays?: number[];
  selectedDays?: number[];
  onToggleDay?: (day: number) => void;
}

export default function CategoriesTable({
  categories,
  competitionId,
  onRowClick,
  onBack,
  emptyMessage,
  availableDays = [],
  selectedDays = [],
  onToggleDay,
}: CategoriesTableProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredCategories = useMemo(() => {
    let filtered = [...categories];

    // Sortowanie alfabetyczne A-Z
    filtered.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      return nameA.localeCompare(nameB, "pl");
    });

    // Filtrowanie po wyszukiwaniu
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (category) =>
          category.name.toLowerCase().includes(term) ||
          category.fullName?.toLowerCase().includes(term),
      );
    }

    if (selectedDays.length > 0) {
      filtered = filtered.filter(
        (category) => typeof category.day === "number" && selectedDays.includes(category.day),
      );
    }

    return filtered;
  }, [categories, searchTerm, selectedDays]);

  const columns = [
    {
      id: "category",
      label: "Kategoria",
      align: "left" as const,
    },
  ];

  return (
    <ModernTable
      data={filteredCategories}
      columns={columns}
      searchPlaceholder="Wyszukaj kategorię..."
      onSearch={setSearchTerm}
      searchValue={searchTerm}
      emptyMessage={searchTerm ? "Nie znaleziono kategorii" : emptyMessage || "Brak kategorii"}
      showBackButton={true}
      onBack={onBack}
      renderRow={(category) => (
        <CategoryTableRow category={category} competitionId={competitionId} />
      )}
      onRowClick={onRowClick}
      getItemKey={(category) => category.categoryId}
      secondaryHeaderActions={
        availableDays.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 0, sm: 0.5 },
              justifyContent: "flex-start",
            }}
          >
            {availableDays.map((day) => (
              <FormControlLabel
                key={day}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedDays.includes(day)}
                    onChange={() => onToggleDay?.(day)}
                    sx={{
                      p: { xs: 0.5, sm: 1 },
                      "& .MuiSvgIcon-root": {
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                      },
                    }}
                  />
                }
                label={`Dzień ${day}`}
                sx={{
                  mr: { xs: 1, sm: 0.5 },
                  "& .MuiFormControlLabel-label": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  },
                }}
              />
            ))}
          </Box>
        ) : undefined
      }
    />
  );
}

